/**
 * MM Admin Partner settlement — fleet view of CP revenue-share runs.
 * MyMoolah own income and sponsor audit evidence live on /admin/audit-evidence.
 * Per-partner drill-through uses the existing mirror Finance overlay.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  FileText,
  RefreshCw,
  Wallet,
  Landmark,
  Receipt,
  AlertCircle,
} from 'lucide-react';
import { KpiMetricCard } from '../dashboard/KpiMetricCard';

const API_BASE = '/api/v1/admin/partner-settlement';
const LIMIT = 20;

interface SettlementRun {
  id: number;
  period: string;
  status: string;
  clientsProcessed: number;
  clientsSucceeded: number;
  clientsFailed: number;
  totalNqrCents: number;
  totalConsiderationCents: number;
  totalFloatCreditedCents: number;
  totalExcessPayableCents: number;
  overStated: boolean;
  startedAt: string | null;
  completedAt: string | null;
}

interface ReconAccount {
  account?: string;
  ledgerCents?: number;
  computedNqrCents?: number;
  nonAttributedCents?: number;
  overStated?: boolean;
}

interface RunDetail extends SettlementRun {
  perClient: Array<{
    clientId: number;
    clientCode?: string;
    companyName?: string;
    status: string;
    error?: string;
    nqrCents?: number;
    considerationCents?: number;
    excessPayableCents?: number;
    invoiceSerial?: string;
  }>;
  reconciliation: {
    overStated?: boolean;
    abortReason?: string;
    accounts?: ReconAccount[];
  };
  excessReport: Array<{ clientId: number; excessPayableCents?: number }>;
}

interface ExcessRow {
  runId: number;
  period: string;
  runStatus: string;
  clientId: number;
  clientCode: string | null;
  companyName: string | null;
  invoiceSerial: string | null;
  excessPayableCents: number;
  bankName: string | null;
  bankAccountLast4: string | null;
  bankDetailsCaptured: boolean;
}

interface InvoiceRow {
  id: number;
  invoiceSerial: string;
  clientId: number;
  clientCode: string;
  companyName: string;
  period: string;
  status: string;
  nqrCents: number;
  revenueSharePct: number | null;
  considerationCents: number;
  exVatCents: number;
  vatCents: number;
  floatCreditedCents: number;
  excessPayableCents: number;
  accrualJournalReference: string | null;
  settlementJournalReference: string | null;
}

interface ExceptionRow {
  runId: number;
  period: string;
  clientId: number;
  clientCode: string | null;
  companyName: string | null;
  error: string;
}

interface Pagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

const STATUS_COLORS: Record<string, string> = {
  running: 'bg-blue-50 text-blue-700',
  completed: 'bg-emerald-50 text-emerald-700',
  completed_with_errors: 'bg-amber-50 text-amber-700',
  failed: 'bg-red-50 text-red-700',
  issued: 'bg-gray-100 text-gray-700',
  emailed: 'bg-emerald-50 text-emerald-700',
  email_failed: 'bg-red-50 text-red-700',
  invoiced: 'bg-emerald-50 text-emerald-700',
  skipped_zero_nqr: 'bg-gray-100 text-gray-600',
  skipped_existing: 'bg-blue-50 text-blue-700',
};

function authHeaders(): HeadersInit {
  const token = sessionStorage.getItem('portal_token');
  const h: Record<string, string> = { Accept: 'application/json' };
  if (token) h.Authorization = `Bearer ${token}`;
  return h;
}

const formatRand = (cents: number) =>
  new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format((cents || 0) / 100);

function formatPeriod(period: string): string {
  const d = new Date(`${period}-01T00:00:00+02:00`);
  return d.toLocaleDateString('en-ZA', { month: 'long', year: 'numeric', timeZone: 'Africa/Johannesburg' });
}

function scrollToId(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function StatusChip({ value }: { value: string }) {
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${STATUS_COLORS[value] || 'bg-gray-100 text-gray-600'}`}>
      {value.replace(/_/g, ' ')}
    </span>
  );
}

export const PartnerSettlementOverlay: React.FC = () => {
  const navigate = useNavigate();
  const [period, setPeriod] = useState('');
  const [error, setError] = useState<string | null>(null);

  const [latestRun, setLatestRun] = useState<SettlementRun | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(true);

  const [runs, setRuns] = useState<SettlementRun[]>([]);
  const [runsPagination, setRunsPagination] = useState<Pagination>({ total: 0, page: 1, limit: LIMIT, totalPages: 0 });
  const [runsPage, setRunsPage] = useState(1);
  const [runsLoading, setRunsLoading] = useState(true);
  const [expandedRunId, setExpandedRunId] = useState<number | null>(null);
  const [runDetail, setRunDetail] = useState<RunDetail | null>(null);
  const [runDetailLoading, setRunDetailLoading] = useState(false);

  const [excessRows, setExcessRows] = useState<ExcessRow[]>([]);
  const [excessTotal, setExcessTotal] = useState(0);
  const [excessLoading, setExcessLoading] = useState(true);

  const [invoices, setInvoices] = useState<InvoiceRow[]>([]);
  const [invoicesPagination, setInvoicesPagination] = useState<Pagination>({ total: 0, page: 1, limit: LIMIT, totalPages: 0 });
  const [invoicesPage, setInvoicesPage] = useState(1);
  const [invoicesLoading, setInvoicesLoading] = useState(true);

  const [exceptions, setExceptions] = useState<ExceptionRow[]>([]);
  const [exceptionsLoading, setExceptionsLoading] = useState(true);

  const periodQuery = period ? `&period=${encodeURIComponent(period)}` : '';

  const fetchOverview = useCallback(async () => {
    setOverviewLoading(true);
    try {
      const res = await fetch(`${API_BASE}/overview`, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to load overview');
      setLatestRun(data.data.latestRun as SettlementRun | null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load overview');
    } finally {
      setOverviewLoading(false);
    }
  }, []);

  const fetchRuns = useCallback(async () => {
    setRunsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/runs?page=${runsPage}&limit=${LIMIT}${periodQuery}`, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to list runs');
      setRuns(data.data.runs as SettlementRun[]);
      setRunsPagination(data.data.pagination as Pagination);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to list runs');
      setRuns([]);
    } finally {
      setRunsLoading(false);
    }
  }, [runsPage, periodQuery]);

  const fetchExcess = useCallback(async () => {
    setExcessLoading(true);
    try {
      const qs = period ? `?period=${encodeURIComponent(period)}` : '';
      const res = await fetch(`${API_BASE}/excess-payouts${qs}`, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to load excess payouts');
      setExcessRows(data.data.rows as ExcessRow[]);
      setExcessTotal(Number(data.data.totalExcessPayableCents || 0));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load excess payouts');
      setExcessRows([]);
    } finally {
      setExcessLoading(false);
    }
  }, [period]);

  const fetchInvoices = useCallback(async () => {
    setInvoicesLoading(true);
    try {
      const res = await fetch(`${API_BASE}/invoices?page=${invoicesPage}&limit=${LIMIT}${periodQuery}`, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to list invoices');
      setInvoices(data.data.invoices as InvoiceRow[]);
      setInvoicesPagination(data.data.pagination as Pagination);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to list invoices');
      setInvoices([]);
    } finally {
      setInvoicesLoading(false);
    }
  }, [invoicesPage, periodQuery]);

  const fetchExceptions = useCallback(async () => {
    setExceptionsLoading(true);
    try {
      const qs = period ? `?period=${encodeURIComponent(period)}` : '';
      const res = await fetch(`${API_BASE}/exceptions${qs}`, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to load exceptions');
      setExceptions(data.data.rows as ExceptionRow[]);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load exceptions');
      setExceptions([]);
    } finally {
      setExceptionsLoading(false);
    }
  }, [period]);

  const refreshAll = useCallback(() => {
    setError(null);
    fetchOverview();
    fetchRuns();
    fetchExcess();
    fetchInvoices();
    fetchExceptions();
  }, [fetchOverview, fetchRuns, fetchExcess, fetchInvoices, fetchExceptions]);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);
  useEffect(() => { fetchRuns(); }, [fetchRuns]);
  useEffect(() => { fetchExcess(); }, [fetchExcess]);
  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);
  useEffect(() => { fetchExceptions(); }, [fetchExceptions]);

  useEffect(() => {
    setRunsPage(1);
    setInvoicesPage(1);
    setExpandedRunId(null);
    setRunDetail(null);
  }, [period]);

  const openRun = async (runId: number) => {
    if (expandedRunId === runId) {
      setExpandedRunId(null);
      setRunDetail(null);
      return;
    }
    setExpandedRunId(runId);
    setRunDetailLoading(true);
    try {
      const res = await fetch(`${API_BASE}/runs/${runId}`, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to load run');
      setRunDetail(data.data as RunDetail);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load run');
      setRunDetail(null);
    } finally {
      setRunDetailLoading(false);
    }
  };

  const openPartnerFinance = (clientId: number) => {
    navigate(`/admin/channel-partners/${clientId}/portal/finance`);
  };

  const kpiPeriod = latestRun ? formatPeriod(latestRun.period) : 'No settlement run yet';

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--foreground)]">Partner settlement</h1>
          <p className="mt-1 max-w-2xl text-sm text-[var(--muted-foreground)]">
            Channel Partner revenue-share fleet: monthly runs, excess above R1,000 (manual bank payout),
            recipient-created tax invoices, and failed partners. This is not MMTP company books.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-xs text-[var(--muted-foreground)]" htmlFor="settlement-period">
            Period
          </label>
          <input
            id="settlement-period"
            type="month"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700"
          />
          {!period && (
            <span className="text-xs text-gray-400">All periods</span>
          )}
          {period && (
            <button
              type="button"
              onClick={() => setPeriod('')}
              className="rounded-xl border border-gray-200 px-3 py-2 text-xs text-gray-600 hover:bg-gray-50"
            >
              All periods
            </button>
          )}
          <button
            type="button"
            onClick={refreshAll}
            className="inline-flex min-h-[40px] items-center gap-2 rounded-xl border border-gray-200 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4" aria-hidden />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden />
          <span>{error}</span>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiMetricCard
          label="Latest run"
          metrics={[{ value: overviewLoading ? '…' : latestRun ? latestRun.status.replace(/_/g, ' ') : '—' }]}
          footnote={kpiPeriod}
          icon={Wallet}
          accent="purple"
          onClick={() => scrollToId('section-runs')}
        />
        <KpiMetricCard
          label="Excess payable"
          metrics={[{ value: excessLoading ? '…' : formatRand(period ? excessTotal : (latestRun?.totalExcessPayableCents ?? 0)) }]}
          footnote="Manual payout — no automated bank rail"
          icon={Landmark}
          accent="orange"
          onClick={() => scrollToId('section-excess')}
        />
        <KpiMetricCard
          label="Failed partners"
          metrics={[{ value: overviewLoading ? '…' : String(period ? exceptions.length : (latestRun?.clientsFailed ?? 0)) }]}
          footnote={latestRun ? `${latestRun.clientsSucceeded}/${latestRun.clientsProcessed} settled` : 'After the monthly job'}
          icon={AlertTriangle}
          accent="blue"
          onClick={() => scrollToId('section-exceptions')}
        />
        <KpiMetricCard
          label="Recon"
          metrics={[{ value: overviewLoading ? '…' : latestRun?.overStated ? 'Over-stated' : latestRun ? 'OK' : '—' }]}
          footnote="Stored control from the run — not a live recompute"
          icon={FileText}
          accent={latestRun?.overStated ? 'orange' : 'green'}
          onClick={() => scrollToId('section-runs')}
        />
      </div>

      <section id="section-runs" className="mymoolah-card overflow-hidden">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <h2 className="text-sm font-semibold text-[var(--foreground)]">Settlement runs</h2>
          <span className="text-xs text-[var(--muted-foreground)]">{runsPagination.total} run(s)</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50 text-left">
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Period</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Status</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Partners</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">NQR</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Share</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Float credited</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Excess</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Recon</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {runsLoading ? (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-gray-400">Loading…</td></tr>
              ) : runs.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-gray-400">
                    No settlement runs yet. The monthly job runs on the 1st at 02:00 SAST.
                  </td>
                </tr>
              ) : (
                runs.map((run) => (
                  <React.Fragment key={run.id}>
                    <tr className="cursor-pointer hover:bg-gray-50" onClick={() => openRun(run.id)}>
                      <td className="px-4 py-3 font-medium text-gray-800">
                        <span className="inline-flex items-center gap-1">
                          {expandedRunId === run.id ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                          {formatPeriod(run.period)}
                        </span>
                      </td>
                      <td className="px-4 py-3"><StatusChip value={run.status} /></td>
                      <td className="px-4 py-3 tabular-nums text-gray-700">
                        {run.clientsSucceeded}/{run.clientsProcessed}
                        {run.clientsFailed > 0 ? ` · ${run.clientsFailed} failed` : ''}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">{formatRand(run.totalNqrCents)}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{formatRand(run.totalConsiderationCents)}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{formatRand(run.totalFloatCreditedCents)}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{formatRand(run.totalExcessPayableCents)}</td>
                      <td className="px-4 py-3">
                        {run.overStated ? <span className="text-xs font-medium text-amber-700">Over-stated</span> : <span className="text-xs text-gray-500">OK</span>}
                      </td>
                    </tr>
                    {expandedRunId === run.id && (
                      <tr>
                        <td colSpan={8} className="bg-gray-50 px-4 py-4">
                          {runDetailLoading || !runDetail ? (
                            <p className="text-xs text-gray-500">Loading run detail…</p>
                          ) : (
                            <div className="space-y-4">
                              {runDetail.reconciliation.abortReason && (
                                <p className="text-xs text-red-700">{runDetail.reconciliation.abortReason}</p>
                              )}
                              {runDetail.reconciliation.accounts && runDetail.reconciliation.accounts.length > 0 && (
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className="text-left text-gray-500">
                                      <th className="py-1">Account</th>
                                      <th className="py-1 text-right">Ledger</th>
                                      <th className="py-1 text-right">Computed NQR</th>
                                      <th className="py-1 text-right">Non-attributed</th>
                                      <th className="py-1">Control</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {runDetail.reconciliation.accounts.map((acc) => (
                                      <tr key={acc.account}>
                                        <td className="py-1 font-mono">{acc.account}</td>
                                        <td className="py-1 text-right tabular-nums">{formatRand(acc.ledgerCents || 0)}</td>
                                        <td className="py-1 text-right tabular-nums">{formatRand(acc.computedNqrCents || 0)}</td>
                                        <td className="py-1 text-right tabular-nums">{formatRand(acc.nonAttributedCents || 0)}</td>
                                        <td className="py-1">{acc.overStated ? 'OVER-STATED' : 'OK'}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="text-left text-gray-500">
                                    <th className="py-1">Partner</th>
                                    <th className="py-1">Status</th>
                                    <th className="py-1 text-right">NQR</th>
                                    <th className="py-1 text-right">Share</th>
                                    <th className="py-1" />
                                  </tr>
                                </thead>
                                <tbody>
                                  {runDetail.perClient.map((row) => (
                                    <tr key={row.clientId}>
                                      <td className="py-1">{row.companyName} <span className="font-mono text-gray-400">({row.clientCode})</span></td>
                                      <td className="py-1">
                                        <StatusChip value={row.status} />
                                        {row.error ? <span className="ml-2 text-red-700">{row.error}</span> : null}
                                      </td>
                                      <td className="py-1 text-right tabular-nums">{row.nqrCents != null ? formatRand(row.nqrCents) : '—'}</td>
                                      <td className="py-1 text-right tabular-nums">{row.considerationCents != null ? formatRand(row.considerationCents) : '—'}</td>
                                      <td className="py-1 text-right">
                                        <button
                                          type="button"
                                          className="inline-flex items-center gap-1 text-[var(--mymoolah-blue)]"
                                          onClick={(e) => { e.stopPropagation(); openPartnerFinance(row.clientId); }}
                                        >
                                          Finance <ExternalLink className="h-3 w-3" />
                                        </button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
        {runsPagination.totalPages > 1 && (
          <div className="flex justify-end gap-2 border-t border-gray-100 px-4 py-3">
            <button type="button" disabled={runsPage <= 1} onClick={() => setRunsPage((p) => p - 1)} className="rounded-lg border px-3 py-1 text-xs disabled:opacity-40">Previous</button>
            <button type="button" disabled={runsPage >= runsPagination.totalPages} onClick={() => setRunsPage((p) => p + 1)} className="rounded-lg border px-3 py-1 text-xs disabled:opacity-40">Next</button>
          </div>
        )}
      </section>

      <section id="section-excess" className="mymoolah-card overflow-hidden">
        <div className="border-b border-gray-100 px-4 py-3">
          <h2 className="text-sm font-semibold text-[var(--foreground)]">Excess payout due</h2>
          <p className="mt-1 text-xs text-[var(--muted-foreground)]">
            Residual above the partner float limit greater than R1,000. Pay manually to the partner bank account within 7 days of month-end. Published CPRSA remains 1.0.0. No automated rail on this screen.
          </p>
        </div>
        {excessLoading ? (
          <p className="px-4 py-12 text-center text-sm text-gray-400">Loading…</p>
        ) : excessRows.length === 0 ? (
          <p className="px-4 py-12 text-center text-sm text-gray-400">No excess payouts due.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50 text-left">
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Period</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Partner</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Invoice</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Bank</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Amount</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {excessRows.map((row) => (
                  <tr key={`${row.runId}-${row.clientId}`} className="border-b border-gray-50">
                    <td className="px-4 py-3">{formatPeriod(row.period)}</td>
                    <td className="px-4 py-3 font-medium">
                      {row.companyName}
                      <span className="ml-1.5 font-mono text-xs text-gray-400">({row.clientCode})</span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{row.invoiceSerial || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">
                      {row.bankDetailsCaptured
                        ? `${row.bankName || ''} ····${row.bankAccountLast4 || ''}`
                        : <span className="font-medium text-red-700">Not captured</span>}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums font-medium">{formatRand(row.excessPayableCents)}</td>
                    <td className="px-4 py-3 text-right">
                      <button type="button" className="inline-flex items-center gap-1 text-xs text-[var(--mymoolah-blue)]" onClick={() => openPartnerFinance(row.clientId)}>
                        Open Finance <ExternalLink className="h-3 w-3" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={4} className="px-4 py-3 text-sm font-semibold">Total due</td>
                  <td className="px-4 py-3 text-right font-bold tabular-nums">{formatRand(excessTotal)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </section>

      <section id="section-invoices" className="mymoolah-card overflow-hidden">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <div className="flex items-center gap-2">
            <Receipt className="h-4 w-4 text-[var(--muted-foreground)]" aria-hidden />
            <h2 className="text-sm font-semibold text-[var(--foreground)]">Invoice register</h2>
          </div>
          <span className="text-xs text-[var(--muted-foreground)]">{invoicesPagination.total} invoice(s)</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50 text-left">
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Serial</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Partner</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Period</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Status</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">NQR</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Share</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">VAT</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {invoicesLoading ? (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-gray-400">Loading…</td></tr>
              ) : invoices.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-gray-400">No tax invoices yet.</td></tr>
              ) : (
                invoices.map((inv) => (
                  <tr key={inv.id} className="border-b border-gray-50">
                    <td className="px-4 py-3 font-mono text-xs">{inv.invoiceSerial}</td>
                    <td className="px-4 py-3">
                      {inv.companyName}
                      <span className="ml-1.5 font-mono text-xs text-gray-400">({inv.clientCode})</span>
                    </td>
                    <td className="px-4 py-3">{formatPeriod(inv.period)}</td>
                    <td className="px-4 py-3"><StatusChip value={inv.status} /></td>
                    <td className="px-4 py-3 text-right tabular-nums">{formatRand(inv.nqrCents)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{formatRand(inv.considerationCents)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{formatRand(inv.vatCents)}</td>
                    <td className="px-4 py-3 text-right">
                      <button type="button" className="inline-flex items-center gap-1 text-xs text-[var(--mymoolah-blue)]" onClick={() => openPartnerFinance(inv.clientId)}>
                        Open Finance <ExternalLink className="h-3 w-3" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {invoicesPagination.totalPages > 1 && (
          <div className="flex justify-end gap-2 border-t border-gray-100 px-4 py-3">
            <button type="button" disabled={invoicesPage <= 1} onClick={() => setInvoicesPage((p) => p - 1)} className="rounded-lg border px-3 py-1 text-xs disabled:opacity-40">Previous</button>
            <button type="button" disabled={invoicesPage >= invoicesPagination.totalPages} onClick={() => setInvoicesPage((p) => p + 1)} className="rounded-lg border px-3 py-1 text-xs disabled:opacity-40">Next</button>
          </div>
        )}
      </section>

      <section id="section-exceptions" className="mymoolah-card overflow-hidden">
        <div className="border-b border-gray-100 px-4 py-3">
          <h2 className="text-sm font-semibold text-[var(--foreground)]">Failed partners</h2>
        </div>
        {exceptionsLoading ? (
          <p className="px-4 py-12 text-center text-sm text-gray-400">Loading…</p>
        ) : exceptions.length === 0 ? (
          <p className="px-4 py-12 text-center text-sm text-gray-400">No failed partners in the selected runs.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50 text-left">
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Period</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Partner</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Error</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {exceptions.map((row) => (
                <tr key={`${row.runId}-${row.clientId}`} className="border-b border-gray-50">
                  <td className="px-4 py-3">{formatPeriod(row.period)}</td>
                  <td className="px-4 py-3 font-medium">
                    {row.companyName}
                    <span className="ml-1.5 font-mono text-xs text-gray-400">({row.clientCode})</span>
                  </td>
                  <td className="px-4 py-3 text-red-700">{row.error}</td>
                  <td className="px-4 py-3 text-right">
                    <button type="button" className="inline-flex items-center gap-1 text-xs text-[var(--mymoolah-blue)]" onClick={() => openPartnerFinance(row.clientId)}>
                      Open Finance <ExternalLink className="h-3 w-3" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
};
